import { useState, useEffect } from 'react';
import { kits as realKits, getRecommendedKits } from '@/data/kits';
import type { Kit } from '@/types/kit';

export interface UseKitDataReturn {
  kits: Kit[];
  recommendedKits: Kit[];
  isLoading: boolean;
  error: string | null;
  getKitById: (id: string) => Kit | undefined;
  getKitsByMaskType: (maskType: 'Nasal' | 'Oronasal') => Kit[];
}

/**
 * Hook for fetching and managing kit data
 * Returns mock data with simulation of loading states
 */
export const useKitData = (maskRecommendation?: string): UseKitDataReturn => {
  const [kits, setKits] = useState<Kit[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchKitData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        // Simulate API call delay
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Use real kit data
        setKits(realKits);
        
      } catch (err) {
        console.error('Error fetching kit data:', err);
        setError('Error cargando kits. Usando datos locales.');
        // Fallback to real data even on error
        setKits(realKits);
      } finally {
        setIsLoading(false);
      }
    };

    fetchKitData();
  }, []);

  // Get recommended kits based on mask recommendation
  const recommendedKits = maskRecommendation 
    ? getRecommendedKits(maskRecommendation)
    : realKits;

  // Helper function to get kit by ID
  const getKitById = (id: string): Kit | undefined => {
    return kits.find(kit => kit.id === id);
  };

  // Helper function to get kits by mask type
  const getKitsByMaskType = (maskType: 'Nasal' | 'Oronasal'): Kit[] => {
    return kits.filter(kit => kit.tipo === maskType);
  };

  return {
    kits,
    recommendedKits,
    isLoading,
    error,
    getKitById,
    getKitsByMaskType
  };
};